import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { faArrowUpRightFromSquare, faCode } from "@fortawesome/pro-regular-svg-icons";

import { LI_POST_QUERY_MAX_LENGTH } from "@signalist/shared-types";

import { classNames } from "@/utils/classNames";

import { Icon } from "@/components/Icon/Icon";

import { LI_POST_QUERY_TOO_LONG_MESSAGE } from "../../schema/liPost";
import { buildLinkedinContentSearchUrl } from "./linkedinSearchUrl";

interface ComposedQueryPreviewProps {
	query: string;
	/** True when showing a preserved free-form/legacy query rather than builder output. */
	isCustom?: boolean;
}

export const ComposedQueryPreview: FunctionComponent<ComposedQueryPreviewProps> = ({
	query,
	isCustom = false,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`liPost.${key}`);

	const trimmed = query.trim();
	const linkedinUrl = buildLinkedinContentSearchUrl(trimmed);
	const isOverLimit = trimmed.length > LI_POST_QUERY_MAX_LENGTH;

	return (
		<div className="mt-2.5 rounded-md border border-border-light bg-blue-action-200 overflow-hidden">
			<div className="flex items-center gap-3 px-2.5 py-2">
				<span className="inline-flex items-center gap-1.5 shrink-0 text-[10px] font-semibold uppercase tracking-wide text-text-disabled">
					<Icon className="text-[11px]" icon={faCode} />
					{ts("composedQueryLabel")}
				</span>

				<div className="flex-1 min-w-0 text-[11.5px] leading-relaxed text-text-meta break-words">
					{trimmed ? (
						trimmed
					) : (
						<span className="italic text-text-disabled">{ts("composedQueryEmpty")}</span>
					)}
				</div>

				<span
					className={classNames(
						"shrink-0 text-[11px] tabular-nums",
						isOverLimit ? "font-medium text-status-error" : "text-text-disabled"
					)}
				>
					{trimmed.length} / {LI_POST_QUERY_MAX_LENGTH}
				</span>

				<a
					className={classNames(
						"inline-flex items-center gap-1.5 shrink-0 px-2.5 py-1 rounded-md border text-[11px] font-medium",
						linkedinUrl
							? "border-blue-action bg-white text-blue-action hover:bg-light-blue"
							: "border-border-light bg-white text-text-disabled opacity-60 pointer-events-none"
					)}
					href={linkedinUrl ?? undefined}
					rel="noopener noreferrer"
					target="_blank"
				>
					<Icon className="text-[11px]" icon={faArrowUpRightFromSquare} />
					{ts("previewOnLinkedin")}
				</a>
			</div>

			{isOverLimit && (
				<div className="px-2.5 pb-2 text-[11px] text-status-error">
					{LI_POST_QUERY_TOO_LONG_MESSAGE}
				</div>
			)}

			{isCustom && (
				<div className="px-2.5 pb-2 text-[11px] text-text-warning">{ts("customQueryNotice")}</div>
			)}
		</div>
	);
};
